// LUMINA · pages/vajra/index.jsx
// Phase 39D: VajraPortal main shell — auth, tab routing, layout
// Refactored from frontend/src/pages/VajraPortal.jsx (5863 → modular)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, GD, GDL, BG, TXT, TXT2, VAJRA_T } from './constants';
import VajraLanding from './VajraLanding';
import PracticeHub from './PracticeHub';
import DharmaHub from './DharmaHub';
import ServiceHub from './ServiceHub';
import DharmaTeachingsHub from './DharmaTeachingsHub';
import VajraSettings from './VajraSettings';
import AdminHub from './AdminHub';
import VajraTeachersPage from './VajraTeachersPage';
import SubscribePage from '../../pages/SubscribePage';
import EnergyInsightCard from './components/EnergyInsightCard';
import GuardianWidget from './components/GuardianWidget';

export default function VajraPortal({ setPage, lang: langProp, setLang }) {
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();

  // Sync lang from URL param (?lang=en|zh) → global setLang, then use global langProp
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && (urlLang === 'en' || urlLang === 'zh') && urlLang !== langProp) {
      setLang && setLang(urlLang);
    }
  // eslint-disable-next-line
  }, []);
  const lang = langProp || 'zh';
  const T = VAJRA_T[lang] || VAJRA_T.zh;

  const [token,  setToken]  = useState(()=>localStorage.getItem("vajra_token")||"");
  const [member, setMember] = useState(null);
  // Phase 39A: legacy tab keys → new 5-tab keys
  const TAB_REDIRECT = { mandala:"practice", sangha:"events", puja:"events", dharma:"events", lineage:"practice", staff:"admin", login:"home", register:"home", offering:"service", treasury:"teachings", shop:"service" };
  const [tab, setTabState] = useState(TAB_REDIRECT[urlTab] || urlTab || "home");
  const setTab = (t) => {
    setTabState(t);
    navigate(t === "home" ? "/vajra" : `/vajra/${t}`, { replace: true });
  };
  useEffect(() => {
    const next = TAB_REDIRECT[urlTab] || urlTab || "home";
    setTabState(prev => prev === next ? prev : next);
  }, [urlTab]);
  const [energyInsight, setEnergyInsight] = React.useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem("vajra_token"));
  const [showEnergyTools, setShowEnergyTools] = useState(() => {
    const stored = localStorage.getItem("vajra_show_energy_tools");
    return stored !== null ? stored === "1" : true;
  });

  // Verify stored token on mount
  useEffect(()=>{
    const t = localStorage.getItem("vajra_token");
    if(!t){setLoading(false);return;}
    fetch(`${API}/vajra/me`,{headers:{"Authorization":`Bearer ${t}`}})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d){
          setMember(d);setToken(t);
          if(d?.is_disciple && localStorage.getItem("vajra_show_energy_tools")===null){
            setShowEnergyTools(false);
            localStorage.setItem("vajra_show_energy_tools","0");
          }
        } else{localStorage.removeItem("vajra_token");setToken("");}
        setLoading(false);
      })
      .catch(()=>{setLoading(false);});
  },[]);

  // Fetch energy insight on token mount (silent LUMINA↔Vajra bridge)
  useEffect(()=>{
    if(!token) return;
    fetch(`${API}/vajra/me/energy-insight`,{headers:{"Authorization":`Bearer ${token}`}})
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d) setEnergyInsight(d); })
      .catch(()=>{});
  },[token]);

  const onLogin  = (m, t) => {
    setMember(m); setToken(t);
    // After login, always redirect to home tab to avoid blank screen
    // when logging in from non-standard routes like /vajra/login
    setTabState("home");
    navigate("/vajra", { replace: true });
    if (m?.is_disciple && localStorage.getItem("vajra_show_energy_tools") === null) {
      setShowEnergyTools(false);
      localStorage.setItem("vajra_show_energy_tools","0");
    }
  };
  const onLogout = () => {
    localStorage.removeItem("vajra_token");
    setToken(""); setMember(null);
  };

  if(loading) return (
    <div style={{minHeight:"100vh",background:BG,display:"flex",alignItems:"center",justifyContent:"center",color:GD,fontSize:14,letterSpacing:2}}>
      {T.loading}
    </div>
  );

  // Tabs: always visible (logged-out users see public tabs, no settings/admin)
  const TABS = [
    { key:"home",      label: T.tabHome },
    { key:"practice",  label: T.tabPractice },
    { key:"events",    label: T.tabEvents },
    { key:"teachers",  label: T.tabTeachers, sub: T.tabTeachersSub },
    { key:"service",   label: T.tabService },
    { key:"teachings", label: T.tabTeachings, sub: T.tabTeachingsSub },
    { key:"subscribe", label: lang==="zh"?"订阅法讯":"Subscribe" },
    ...(member?.is_staff ? [{ key:"admin", label: T.tabAdmin }] : []),
  ];

  const toggleLang = () => {
    const next = lang === "zh" ? "en" : "zh";
    setLang && setLang(next);
  };

  return (
    <div style={{minHeight:"100vh",background:BG,fontFamily:"'Noto Serif SC','Noto Serif',serif",color:TXT}}>
      {/* Header */}
      <div style={{
        background:"rgba(253,251,247,0.97)",padding:"16px 28px 0",
        borderBottom:`1px solid rgba(212,175,55,0.28)`,
        boxShadow:"0 2px 12px rgba(139,105,20,0.07)",
        backdropFilter:"blur(16px)",position:"sticky",top:0,zIndex:100}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:16}}>
              <div>
                <div style={{color:GD,fontSize:22,letterSpacing:4,fontFamily:"'Noto Serif SC','Noto Serif',serif"}}>{T.brandName}</div>
                <div style={{color:TXT2,fontSize:11,letterSpacing:2,marginTop:2,opacity:0.8}}>{T.brandSub}</div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              {/* Language toggle */}
              <button onClick={toggleLang} style={{
                background:"transparent",border:`1px solid rgba(139,105,20,0.3)`,borderRadius:20,
                color:GDL,fontSize:12,padding:"4px 12px",cursor:"pointer",letterSpacing:.5,
                fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
                {lang==="zh" ? "English" : "中文"}
              </button>
              {member ? (
                <>
                  {member.is_staff&&(
                    <span style={{fontSize:10,color:GD,border:`1px solid rgba(212,175,55,0.4)`,borderRadius:10,padding:"2px 10px",letterSpacing:.5}}>{T.staff}</span>
                  )}
                  {member.vajra_tier && member.vajra_tier!=="free" && (
                    <span style={{fontSize:10,color:"#7c3aed",border:"1px solid rgba(124,58,237,0.3)",borderRadius:10,padding:"2px 10px",letterSpacing:.5,textTransform:"uppercase"}}>
                      {member.vajra_tier}
                    </span>
                  )}
                  <button onClick={()=>setTab("settings")} title={T.tabSettings} style={{
                    background:"none",border:"none",cursor:"pointer",
                    fontSize:16,lineHeight:"16px",padding:"4px 6px",
                    color:tab==="settings"?GD:TXT2,opacity:tab==="settings"?1:0.6,
                    transition:"color .2s"
                  }}>
                    ⚙️
                  </button>
                  <span style={{color:TXT2,fontSize:12,fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>{member.dharma_name||member.name}</span>
                  <button onClick={onLogout} style={{background:"none",border:`1px solid rgba(212,175,55,0.35)`,borderRadius:6,color:TXT2,fontSize:11,padding:"4px 10px",cursor:"pointer",letterSpacing:.5,fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>{T.signOut}</button>
                </>
              ) : (
                <button onClick={() => { navigate("/vajra?auth=1"); }} style={{
                  background:`linear-gradient(135deg,${GD},#A07828)`,border:"none",borderRadius:8,
                  color:"#fff",fontSize:12,padding:"7px 20px",cursor:"pointer",letterSpacing:1,
                  fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif"}}>
                  {T.loginRequired === "Login Required" ? "Login / Register" : "登录 / 注册"}
                </button>
              )}
            </div>
          </div>
          {/* Tab Nav */}
          <div style={{display:"flex",gap:0,overflowX:"auto"}}>
            {TABS.map(t=>(
              <button key={t.key} onClick={()=>{ if(t.external&&setPage) setPage(t.key); else setTab(t.key); }}
                style={{padding:"10px 22px",background:"none",border:"none",cursor:"pointer",
                  fontSize:14,fontWeight:500,letterSpacing:.5,whiteSpace:"nowrap",
                  fontFamily:"'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif",
                  color:tab===t.key?GD:TXT2,
                  opacity:tab===t.key?1:0.75,
                  borderBottom:tab===t.key?`2px solid ${GD}`:"2px solid transparent",
                  marginBottom:-1,transition:"color .2s,opacity .2s"}}>
                <div>{t.label}</div>
                {t.sub && <div style={{fontSize:9,letterSpacing:.3,opacity:.6,marginTop:1}}>{t.sub}</div>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Floating Energy Insight Card (non-home tabs) */}
      {tab!=="home" && showEnergyTools && energyInsight && energyInsight.has_report && (
        <EnergyInsightCard insight={energyInsight}/>
      )}

      {/* Content */}
      <div style={{maxWidth:tab==="home"?1100:tab==="admin"?1200:760,margin:"0 auto",padding:"0 24px 60px",transition:"max-width .3s"}}>
        {tab==="home"     && <VajraLanding member={member} onLogin={onLogin} setPage={setPage} lang={lang} T={T} setLang={setLang}/>}
        {tab==="practice" && <PracticeHub token={token} member={member} lang={lang} T={T}/>}
        {tab==="events"    && <DharmaHub token={token} member={member} lang={lang} T={T}/>}
        {tab==="teachers"  && <VajraTeachersPage setPage={setPage} member={member} lang={lang} T={T}/>}
        {tab==="service"   && <ServiceHub token={token} member={member} lang={lang} T={T}/>}
        {tab==="teachings" && null /* redirected to /vajra/teachings via setTab → App.jsx route */}
        {tab==="subscribe" && <SubscribePage/>}
        {tab==="settings"  && <VajraSettings member={member} showEnergyTools={showEnergyTools} setShowEnergyTools={setShowEnergyTools} token={token} lang={lang} T={T} setLang={setLang}/>}
        {tab==="admin"    && member?.is_staff && <AdminHub token={token} lang={lang} T={T}/>}
      </div>

      {/* Guardian floating widget — visible from all tabs */}
      <GuardianWidget token={token} member={member} energyInsight={showEnergyTools?energyInsight:null}/>
    </div>
  );
}
